import { useEffect } from 'react';
import type { ReactNode } from 'react';
import usePageTranslation from '../../../hooks/ui/usePageTranslation';
import { getTranslatedTextOrDefault } from '../../../util/util';
import { useMobilePreview } from '../hooks/use-mobile-preview';

interface MobilePreviewShellProps {
  children: ReactNode;
}

const HTML_PREVIEW_CLASS = 'bc-mobile-preview-active';

export function MobilePreviewShell({ children }: MobilePreviewShellProps) {
  const { isMobilePreview, disable } = useMobilePreview();
  const PageTranslation = usePageTranslation();
  const translations = Array.isArray(PageTranslation) ? PageTranslation : [];
  const stageLabel = getTranslatedTextOrDefault(
    'mobilePreview.stage.label',
    translations,
    'Mobile preview of the current page',
  );
  const closeLabel = getTranslatedTextOrDefault(
    'mobilePreview.close.tooltip',
    translations,
    'Return to normal view',
  );

  useEffect(() => {
    document.documentElement.classList.toggle(HTML_PREVIEW_CLASS, isMobilePreview);
    return () => {
      document.documentElement.classList.remove(HTML_PREVIEW_CLASS);
    };
  }, [isMobilePreview]);

  useEffect(() => {
    if (!isMobilePreview) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        disable();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isMobilePreview, disable]);

  return (
    <div className={isMobilePreview ? 'bc-mobile-preview' : 'bc-mobile-preview bc-mobile-preview--off'}>
      <button
        type="button"
        className="bc-mobile-preview__backdrop"
        hidden={!isMobilePreview}
        tabIndex={isMobilePreview ? 0 : -1}
        aria-hidden={!isMobilePreview}
        aria-label={closeLabel}
        onClick={disable}
      />
      <div
        className={isMobilePreview ? 'bc-mobile-preview__stage' : 'bc-mobile-preview__host'}
        {...(isMobilePreview
          ? { role: 'region', 'aria-label': stageLabel, 'data-testid': 'mobile-preview-stage' }
          : {})}
      >
        {children}
      </div>
    </div>
  );
}
